import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ShieldCheck, Users, TrendingUp, Sparkles } from "lucide-react";
import { PixPaymentBox } from "@/components/SiteConfig";

function PlanCard({ p, ctaTo, highlight }) {
  return (
    <div className={`rounded-2xl p-6 border-2 ${highlight?"border-[#0D7C66] bg-white":"border-slate-200 bg-white"} gs-shadow`}>
      {highlight && <Badge className="bg-[#0D7C66] text-white border-0 mb-2">Mais escolhido</Badge>}
      <div className="text-sm uppercase tracking-widest text-slate-500 font-semibold">{p.name}</div>
      <div className="mt-2 text-4xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>R$ {p.price.toFixed(2).replace(".",",")}<span className="text-base text-slate-500 font-normal">/{p.period}</span></div>
      <ul className="mt-4 space-y-2 text-sm text-slate-700">
        {p.features.map(f=>(<li key={f} className="flex gap-2"><Check className="w-4 h-4 text-[#0D7C66] mt-0.5"/>{f}</li>))}
      </ul>
      <Button data-testid={`plan-cta-${p.id}`} className={`mt-5 w-full ${highlight?"bg-[#0D7C66] hover:bg-[#074E41]":"bg-[#0F172A] hover:bg-black"}`} asChild>
        <Link to={ctaTo}>Escolher {p.name}</Link>
      </Button>
    </div>
  );
}

export function SejaAssociado() {
  const [plans, setPlans] = useState([]);
  const [visits, setVisits] = useState(12);
  useEffect(()=>{ api.get("/plans",{params:{audience:"associate"}}).then(r=>setPlans(r.data)); }, []);
  const avgTicket = 220;
  const discountAvg = 0.25;
  const monthly = 39.9;
  const economy = Math.max(0, visits * avgTicket * discountAvg - monthly * 12);
  return (
    <PublicLayout>
      <section className="gs-hero-bg text-white py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge className="bg-white/15 text-white border-0 mb-4">Para você e sua família</Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold" style={{fontFamily:"Plus Jakarta Sans"}}>Seja Associado GIRO+SAÚDE</h1>
            <p className="mt-4 text-white/85 max-w-lg">Uma rede regional de profissionais e clínicas com benefícios exclusivos, carteira digital e acesso rápido a quem cuida da sua saúde.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button data-testid="assoc-cta-top" size="lg" className="bg-[#FF7D54] hover:bg-[#e56a44]" asChild><Link to="/cadastro?role=associate">Quero ser associado</Link></Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              {i:ShieldCheck,t:"Rede validada",d:"Profissionais e clínicas verificados"},
              {i:Sparkles,t:"Benefícios exclusivos",d:"Descontos e condições especiais"},
              {i:Users,t:"Família junto",d:"Plano familiar com dependentes"},
              {i:TrendingUp,t:"Regional",d:"Foco na Baixada Santista"},
            ].map((x)=>(
              <div key={x.t} className="bg-white/10 rounded-2xl p-5">
                <x.i className="w-6 h-6 mb-2"/>
                <div className="font-semibold">{x.t}</div>
                <div className="text-sm text-white/80">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="rounded-3xl bg-white border border-slate-200 gs-shadow p-8 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#FF7D54] font-semibold">Calculadora</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold mt-1" style={{fontFamily:"Plus Jakarta Sans"}}>Quanto você economiza por ano?</h2>
            <p className="text-sm text-slate-600 mt-2">Estimativa considerando ticket médio de R$ 220 por atendimento e 25% de desconto médio na rede.</p>
            <div className="mt-5">
              <label className="text-sm text-slate-600">Consultas/exames por ano: <b>{visits}</b></label>
              <input data-testid="calc-visits" type="range" min="1" max="24" value={visits} onChange={e=>setVisits(+e.target.value)} className="w-full accent-[#0D7C66] mt-2"/>
            </div>
          </div>
          <div className="rounded-2xl gs-warm-bg p-6 text-center">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Economia estimada/ano</div>
            <div className="text-5xl font-extrabold text-[#0D7C66] mt-2" style={{fontFamily:"Plus Jakarta Sans"}} data-testid="calc-economy">R$ {economy.toFixed(2).replace(".",",")}</div>
            <div className="text-xs text-slate-500 mt-2">Já descontado o valor da assinatura Essencial (R$ 39,90/mês).</div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-widest text-[#0D7C66] font-semibold text-center">Planos</div>
        <h2 className="text-3xl font-extrabold text-center mb-10" style={{fontFamily:"Plus Jakarta Sans"}}>Escolha o seu</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p,i)=><PlanCard key={p.id} p={p} highlight={p.id==="assoc-familia"} ctaTo={`/cadastro?role=associate&plan=${p.id}`}/>)}
        </div>

        <div className="mt-10">
          <PixPaymentBox amount="39,90" description="Assinatura mensal do Plano Essencial. Após o Pix, envie o comprovante pelo WhatsApp para ativarmos sua carteira."/>
        </div>
      </section>
    </PublicLayout>
  );
}

export function SejaParceiro() {
  const [plans, setPlans] = useState([]);
  const [clinPlans, setClinPlans] = useState([]);
  useEffect(()=>{
    api.get("/plans",{params:{audience:"professional"}}).then(r=>setPlans(r.data));
    api.get("/plans",{params:{audience:"clinic"}}).then(r=>setClinPlans(r.data));
  }, []);
  return (
    <PublicLayout>
      <section className="bg-[#0F172A] text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <Badge className="bg-[#FF7D54] text-white border-0 mb-4">Profissionais e clínicas</Badge>
          <h1 className="text-4xl sm:text-5xl font-extrabold max-w-3xl" style={{fontFamily:"Plus Jakarta Sans"}}>Divulgue seu trabalho e receba oportunidades reais</h1>
          <p className="mt-4 text-white/80 max-w-2xl">Fortaleça sua presença digital na Baixada Santista, apareça nas buscas e receba leads qualificados de pessoas interessadas em seus serviços.</p>
          <Button data-testid="parc-cta-top" size="lg" className="mt-6 bg-[#FF7D54] hover:bg-[#e56a44]" asChild><Link to="/cadastro?role=professional">Quero ser parceiro</Link></Button>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-14">
        <h2 className="text-2xl font-bold mb-6" style={{fontFamily:"Plus Jakarta Sans"}}>Planos para profissionais</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p,i)=><PlanCard key={p.id} p={p} highlight={i===1} ctaTo={`/cadastro?role=professional&plan=${p.id}`}/>)}
        </div>

        <h2 className="text-2xl font-bold mt-16 mb-6" style={{fontFamily:"Plus Jakarta Sans"}}>Planos para clínicas</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {clinPlans.map((p,i)=><PlanCard key={p.id} p={p} highlight={i===0} ctaTo={`/cadastro?role=clinic&plan=${p.id}`}/>)}
        </div>

        <div className="mt-10 max-w-xl">
          <PixPaymentBox description="Pagamento da mensalidade do plano profissional/clínica via Pix. Envie o comprovante pelo WhatsApp para ativação imediata."/>
        </div>
      </section>
    </PublicLayout>
  );
}
